// @ts-nocheck
// =============================================================================
//  KitchenControl — Receptenbeheer Module
//  Props: { sb, tenantId, activeTab, onTabChange }
//  activeTab: 'recepten' | 'nieuw' | 'paste' | 'categorieen'
// =============================================================================
import { useState, useEffect, useRef, useCallback } from "react";
import {
  BookOpen, Plus, ClipboardPaste, Settings2,
  X, Trash2, Search, ChevronDown, ChevronRight,
  TriangleAlert, Save, Camera,
} from "lucide-react";

// ── Kleuren ───────────────────────────────────────────────────────────────────
const C = {
  bg:      "#F8FAFC",
  bg2:     "#FFFFFF",
  bg3:     "#F1F5F9",
  hdr:     "#0F172A",
  hdrBdr:  "#1E293B",
  txt:     "#0F172A",
  txt2:    "#475569",
  txt3:    "#94A3B8",
  bdr:     "#E2E8F0",
  bdr2:    "#CBD5E1",
  pri:     "#2563EB",
  priBg:   "#EFF6FF",
  priBd:   "#BFDBFE",
  green:   "#16A34A",
  greenBg: "#DCFCE7",
  greenBd: "#BBF7D0",
  red:     "#DC2626",
  redBg:   "#FEE2E2",
  redBd:   "#FCA5A5",
  amber:   "#D97706",
  amberBg: "#FFFBEB",
  amberBd: "#FDE68A",
};

// ── Constanten ────────────────────────────────────────────────────────────────
const uid = () => `${Date.now()}-${Math.floor(Math.random() * 9999)}`;
const LS_KEY = "kc-recepten-v1";

const ALLERGENEN_LIJST = [
  "Gluten","Schaaldieren","Eieren","Vis","Pindas","Soja","Melk","Noten",
  "Selderij","Mosterd","Sesamzaad","Zwaveldioxide","Lupine","Weekdieren",
];

const BEWAARADVIES_OPTIES = [
  "", "Bewaren onder 7°C", "Bewaren onder -18°C",
  "Bewaren op kamertemperatuur", "Direct serveren",
];

const UNITS = ["kg","g","L","ml","stuks","bos","blik","zak","el","tl","snuf","teen","takje","ons","overig"];

// ── Gedeelde stijlen ──────────────────────────────────────────────────────────
const inp = {
  background: C.bg2, border: `1px solid ${C.bdr}`, color: C.txt,
  padding: "7px 10px", fontFamily: "inherit", fontSize: 13,
  borderRadius: 6, outline: "none", width: "100%", boxSizing: "border-box",
};
const btnStyle = (bg, fg, bd) => ({
  background: bg, color: fg, border: `1px solid ${bd || bg}`,
  padding: "7px 14px", fontFamily: "inherit", fontSize: 12,
  fontWeight: 600, borderRadius: 6, cursor: "pointer", display: "flex",
  alignItems: "center", gap: 5,
});

// ── Paste-parser (overgenomen uit BijHENC) ────────────────────────────────────
const PARSE_UNITS = ["kg","g","gr","gram","ml","l","dl","cl","liter","el","tl",
  "eetlepel","theelepel","stuks","stuk","blik","blikken","teen","teentje",
  "teentjes","takje","takjes","snuf","snufje","ons","pond"];
const UNIT_NORM = {
  gr:"g", gram:"g", liter:"L", dl:"dl", cl:"cl", eetlepel:"el",
  theelepel:"tl", stuk:"stuks", blikken:"blik", teentje:"teen",
  teentjes:"teen", takjes:"takje", snufje:"snuf",
};

function parseIngLine(line) {
  line = line.trim().replace(/^[-–•*]\s*/, "");
  if (!line || line.length < 2) return null;
  const numRx = /^(\d+(?:[.,]\d+)?)\s*/;
  const unitRxStr = "^(" + PARSE_UNITS.join("|") + ")\\b\\.?\\s*";
  const unitRx = new RegExp(unitRxStr, "i");
  let rest = line, amount = "", unit = "";
  const numMatch = rest.match(numRx);
  if (numMatch) {
    amount = numMatch[1].replace(",", ".");
    rest = rest.slice(numMatch[0].length);
    const um = rest.match(unitRx);
    if (um) { unit = UNIT_NORM[um[1].toLowerCase()] || um[1].toLowerCase(); rest = rest.slice(um[0].length); }
    else unit = "stuks";
  } else {
    const um = rest.match(unitRx);
    if (um) { unit = UNIT_NORM[um[1].toLowerCase()] || um[1].toLowerCase(); rest = rest.slice(um[0].length); }
    else return null;
  }
  const name = rest.trim();
  if (!name || name.length < 2) return null;
  return { id: uid(), naam: name, hoeveelheid: amount || "1", eenheid: unit };
}

function parsePasteRecept(text) {
  const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
  if (!lines.length) return null;
  const titel = lines[0];
  const ingredienten = [], bereidingLines = [];
  for (const line of lines.slice(1)) {
    const ing = parseIngLine(line);
    if (ing) ingredienten.push(ing);
    else if (line.length > 3) bereidingLines.push(line);
  }
  return { naam: titel, ingredienten, bereiding: bereidingLines.join("\n") };
}

// ── Allergeen-detectie (overgenomen uit BijHENC) ──────────────────────────────
const ALLERGEN_KEYWORDS = {
  Gluten: { include: ["tarwe","tarwemeel","bloem","bakmeel","brood","paneermeel","pasta","noedels","spaghetti","couscous","bulgur","spelt","rogge","gerst","haver","havermout","crackers","bladerdeeg","ketjap","sojasaus","wrap","tortilla"], exclude: [] },
  Schaaldieren: { include: ["garnaal","garnalen","scampi","langoustine","kreeft","krab","rivierkreeft","crevette"], exclude: [] },
  Eieren: { include: ["ei","eieren","eigeel","eidooier","eiwit","eipoeder","mayonaise","hollandaise","bearnaise","aioli","meringue","custard"], exclude: [] },
  Vis: { include: ["vis","tonijn","zalm","kabeljauw","schol","tong","wijting","makreel","haring","sardine","ansjovis","forel","zeebaars","dorade","vissaus","worcestershire","surimi","kaviaar"], exclude: [] },
  Pindas: { include: ["pinda","pindas","pindakaas","pindasaus","satésaus","aardnoot","arachide"], exclude: [] },
  Soja: { include: ["soja","sojaboon","sojasaus","ketjap","edamame","tofu","tempeh","miso","sojamelk","tahini"], exclude: [] },
  Melk: { include: ["melk","room","slagroom","crème fraîche","mascarpone","ricotta","mozzarella","parmezaan","kaas","boter","ghee","yoghurt","kwark","zuivel","melkpoeder","roomijs"], exclude: ["rijstmelk","sojamelk","amandelmelk","kokosmelk","havermelk","olijfboter","pindakaas","botersla"] },
  Noten: { include: ["amandel","amandelen","hazelnoot","walnoot","cashew","pecan","paranoot","pistache","macadamia","marsepein","nougat","pijnboompitten","noten"], exclude: ["nootmuskaat","kokosnoot"] },
  Selderij: { include: ["selderij","bleekselderij","knolselderij","selderie"], exclude: [] },
  Mosterd: { include: ["mosterd","mosterdzaad","dijon"], exclude: [] },
  Sesamzaad: { include: ["sesam","sesamzaad","sesamolie","tahini","tahin"], exclude: [] },
  Zwaveldioxide: { include: ["wijn","port","sherry","marsala","vermouth","azijn","balsamico","rozijnen","krenten","gedroogd fruit"], exclude: ["rijstazijn","appelazijn"] },
  Lupine: { include: ["lupine","lupinebonen","lupinemeel"], exclude: [] },
  Weekdieren: { include: ["mossel","mosselen","oester","oesters","inktvis","calamares","octopus","coquille","wulk","kokkel"], exclude: [] },
};

const GLUTEN_SUB = ["Tarwe", "Rogge", "Gerst", "Haver"];
const NOTEN_SUB = ["Amandelen", "Hazelnoten", "Walnoten", "Cashewnoten", "Pecannoten", "Paranoten", "Pistachenoten", "Macadamianoten"];

function detectAllergenen(ingredienten) {
  if (!Array.isArray(ingredienten) || !ingredienten.length) return [];
  const norm = s => (s||"").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"").replace(/\s+/g," ").trim();
  const esc = s => s.replace(/[.*+?^${}()|[\]\\]/g,"\\$&");
  const wb = t => new RegExp(`\\b${esc(norm(t))}\\b`);
  const text = ingredienten.map(i => norm(i.naam)).join(" | ");
  const detected = [];

  for (const [allergeen, { include, exclude }] of Object.entries(ALLERGEN_KEYWORDS)) {
    const excl = exclude.some(t => wb(t).test(text));
    let found = include.some(t => wb(t).test(text));
    if (found && !excl) {
      detected.push(allergeen);
    } else if (found && excl) {
      const clean = exclude.reduce((acc, ex) => acc.replace(wb(ex)," "), text);
      if (include.some(t => wb(t).test(clean))) detected.push(allergeen);
    }
  }

  // Sub-detectie Gluten
  if (detected.includes("Gluten")) {
    const subMap = {
      Tarwe:  ["tarwe","tarwemeel","bloem","bakmeel","semolina","griesmeel","spelt","kamut","brood","paneermeel","pasta","noedels","spaghetti","couscous","bulgur","seitan","bladerdeeg","crackers","wrap","tortilla"],
      Rogge:  ["rogge","roggebrood","roggemeel"],
      Gerst:  ["gerst","gort","parelgort","mout"],
      Haver:  ["haver","havermout","havervlokken"],
    };
    for (const [sub, terms] of Object.entries(subMap)) {
      if (terms.some(t => wb(t).test(text))) detected.push(sub);
    }
  }

  // Sub-detectie Noten
  if (detected.includes("Noten")) {
    const subMap = {
      Amandelen:      ["amandel","amandelen","amandelmeel","marsepein","nougat","praline"],
      Hazelnoten:     ["hazelnoot","hazelnoten","nutella"],
      Walnoten:       ["walnoot","walnoten"],
      Cashewnoten:    ["cashew","cashewnoten"],
      Pecannoten:     ["pecan","pecannoten"],
      Paranoten:      ["paranoot","paranoten"],
      Pistachenoten:  ["pistache","pistaches"],
      Macadamianoten: ["macadamia"],
    };
    for (const [sub, terms] of Object.entries(subMap)) {
      if (terms.some(t => wb(t).test(text))) detected.push(sub);
    }
  }

  return detected;
}

// ── Supabase helpers ──────────────────────────────────────────────────────────
const LS_KEY_RECEPTEN = "kc-recepten-v1";

function loadLocal() {
  try { const s = localStorage.getItem(LS_KEY_RECEPTEN); if (s) return JSON.parse(s); } catch {}
  return null;
}
function saveLocal(state) {
  try { localStorage.setItem(LS_KEY_RECEPTEN, JSON.stringify(state)); } catch {}
}

async function sbLoad(sb, tenantId) {
  try {
    const rows = await sb.select("recepten_data", `tenant_id=eq.${tenantId}&select=data&limit=1`);
    if (rows?.length && rows[0]?.data) return rows[0].data;
  } catch (e) { console.warn("[Recepten] load failed:", e); }
  return null;
}

async function sbSave(sb, tenantId, data) {
  try {
    const exists = await sb.select("recepten_data", `tenant_id=eq.${tenantId}&select=tenant_id&limit=1`);
    if (exists?.length) {
      await sb.update("recepten_data", `tenant_id=eq.${tenantId}`, { data, updated_at: new Date().toISOString() });
    } else {
      await sb.insert("recepten_data", { tenant_id: tenantId, data, updated_at: new Date().toISOString() });
    }
  } catch (e) { console.warn("[Recepten] save failed:", e); }
}

function initState() {
  const saved = loadLocal();
  if (saved?.recepten) return saved;
  return { recepten: [], categorieen: ["Vlees","Vis","Soepen","Sauzen","Dessert","Overig"] };
}

// ─────────────────────────────────────────────────────────────────────────────
// RECEPTEN MODULE
// ─────────────────────────────────────────────────────────────────────────────
export default function ReceptenModule({ sb, tenantId, activeTab, onTabChange, openReceptId, onOpenReceptHandled }) {
  const [state, setState] = useState(initState);
  const [selId, setSelId] = useState(null);        // geopend recept

  // Extern aansturen: open direct een specifiek recept
  useEffect(() => {
    if (openReceptId) {
      setSelId(openReceptId);
      onOpenReceptHandled?.();
    }
  }, [openReceptId]);
  const [syncStatus, setSyncStatus] = useState("local");
  const saveTimerRef = useRef(null);
  const localRef = useRef(false);
  const pollRef = useRef(null);

  const tab = activeTab || "recepten";
  const { recepten, categorieen } = state;

  // ── Persistentie ────────────────────────────────────────────────────────────
  const update = useCallback((fn) => setState(s => {
    const next = { ...s, ...fn(s) };
    saveLocal(next);
    if (sb && tenantId) {
      localRef.current = true;
      setSyncStatus("syncing");
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(async () => {
        await sbSave(sb, tenantId, next);
        setSyncStatus("synced");
        setTimeout(() => { localRef.current = false; }, 500);
      }, 600);
    }
    return next;
  }), [sb, tenantId]);

  // ── Supabase laden + polling ────────────────────────────────────────────────
  useEffect(() => {
    if (!sb || !tenantId) return;
    let lastUpdatedAt = null;

    (async () => {
      setSyncStatus("syncing");
      const remote = await sbLoad(sb, tenantId);
      if (remote?.recepten) {
        setState(local => {
          const merged = { ...local, ...remote };
          saveLocal(merged);
          return merged;
        });
      }
      setSyncStatus("synced");
    })();

    const poll = async () => {
      if (localRef.current) return;
      try {
        const rows = await sb.select("recepten_data", `tenant_id=eq.${tenantId}&select=data,updated_at&limit=1`);
        if (!rows?.length) return;
        const row = rows[0];
        if (lastUpdatedAt && row.updated_at === lastUpdatedAt) return;
        if (!lastUpdatedAt) { lastUpdatedAt = row.updated_at; return; }
        lastUpdatedAt = row.updated_at;
        setState(local => {
          const merged = { ...local, ...row.data };
          saveLocal(merged);
          return merged;
        });
      } catch {}
    };

    pollRef.current = setInterval(poll, 2000);
    poll();

    return () => {
      clearInterval(pollRef.current);
      clearTimeout(saveTimerRef.current);
    };
  }, [sb, tenantId]);

  // ── CRUD ────────────────────────────────────────────────────────────────────
  const slaRecept = (recept) => {
    update(s => ({
      recepten: s.recepten.find(r => r.id === recept.id)
        ? s.recepten.map(r => r.id === recept.id ? recept : r)
        : [...s.recepten, recept]
    }));
  };
  const verwijderRecept = (id) => {
    update(s => ({ recepten: s.recepten.filter(r => r.id !== id) }));
    if (selId === id) setSelId(null);
  };
  const archiveerRecept = (id, archived) => {
    update(s => ({ recepten: s.recepten.map(r => r.id === id ? { ...r, archived } : r) }));
  };

  // Navigeer naar 'recepten' na opslaan via paste/nieuw
  const handleSaveAndGo = (recept) => {
    slaRecept(recept);
    setSelId(recept.id);
    onTabChange?.("recepten");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: C.bg, fontFamily: "system-ui,-apple-system,sans-serif", fontSize: 13, color: C.txt, overflow: "hidden" }}>

      {/* ── Header ── */}
      <header style={{ background: C.hdr, borderBottom: `1px solid ${C.hdrBdr}`, height: 52, display: "flex", alignItems: "center", padding: "0 18px", gap: 12, flexShrink: 0 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>
          {tab === "paste" ? "🍽 Recept plakken" : tab === "nieuw" ? "➕ Nieuw recept" : tab === "categorieen" ? "⚙️ Categorieën" : "📖 Recepten"}
        </span>
        <div style={{ flex: 1 }} />
        {sb && (
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: syncStatus === "syncing" ? "#FCD34D" : "rgba(255,255,255,.45)" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: syncStatus === "syncing" ? "#FCD34D" : "#4ADE80" }} />
            {syncStatus === "syncing" ? "synchroniseren…" : "gesynchroniseerd"}
          </div>
        )}
      </header>

      {/* ── Content ── */}
      <div style={{ flex: 1, overflow: "hidden", display: "flex" }}>
        {tab === "recepten" && (
          <ReceptenLijst
            recepten={recepten}
            categorieen={categorieen}
            selId={selId}
            setSelId={setSelId}
            onSave={slaRecept}
            onDelete={verwijderRecept}
            onArchive={archiveerRecept}
          />
        )}
        {tab === "nieuw" && (
          <ReceptForm
            recept={null}
            categorieen={categorieen}
            onSave={handleSaveAndGo}
            onCancel={() => onTabChange?.("recepten")}
          />
        )}
        {tab === "paste" && (
          <PasteView
            categorieen={categorieen}
            onSave={handleSaveAndGo}
            onCancel={() => onTabChange?.("recepten")}
          />
        )}
        {tab === "categorieen" && (
          <CategorieenView
            categorieen={categorieen}
            onUpdate={(cats) => update(() => ({ categorieen: cats }))}
          />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RECEPTEN LIJST
// ─────────────────────────────────────────────────────────────────────────────
function ReceptenLijst({ recepten, categorieen, selId, setSelId, onSave, onDelete, onArchive }) {
  const [zoek, setZoek] = useState("");
  const [catFilter, setCatFilter] = useState("alle");
  const [toonArchief, setToonArchief] = useState(false);

  const zichtbaar = recepten
    .filter(r => toonArchief ? r.archived : !r.archived)
    .filter(r => catFilter === "alle" || r.categorie === catFilter)
    .filter(r => !zoek || r.naam.toLowerCase().includes(zoek.toLowerCase()))
    .sort((a, b) => a.naam.localeCompare(b.naam, "nl"));

  const geselecteerd = selId ? recepten.find(r => r.id === selId) : null;

  return (
    <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
      {/* Linker paneel — lijst */}
      <div style={{ width: geselecteerd ? 320 : "100%", minWidth: 260, borderRight: geselecteerd ? `1px solid ${C.bdr}` : "none", display: "flex", flexDirection: "column", overflow: "hidden", transition: "width .2s" }}>
        {/* Zoek + filter */}
        <div style={{ padding: "12px 14px", borderBottom: `1px solid ${C.bdr}`, display: "flex", flexDirection: "column", gap: 8, background: C.bg2 }}>
          <div style={{ position: "relative" }}>
            <Search size={13} style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: C.txt3, pointerEvents: "none" }} />
            <input style={{ ...inp, paddingLeft: 28, fontSize: 12 }} placeholder="Zoeken…" value={zoek} onChange={e => setZoek(e.target.value)} />
          </div>
          <div style={{ display: "flex", gap: 6, overflowX: "auto" }}>
            {["alle", ...categorieen].map(cat => (
              <button key={cat} onClick={() => setCatFilter(cat)}
                style={{ padding: "4px 10px", fontSize: 11, fontFamily: "inherit", fontWeight: catFilter === cat ? 700 : 400, cursor: "pointer", borderRadius: 12, border: `1px solid ${catFilter === cat ? C.pri : C.bdr}`, background: catFilter === cat ? C.priBg : "transparent", color: catFilter === cat ? C.pri : C.txt2, whiteSpace: "nowrap" }}>
                {cat === "alle" ? "Alle" : cat}
              </button>
            ))}
          </div>
        </div>

        {/* Lijst */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {zichtbaar.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: C.txt3 }}>
              <BookOpen size={36} strokeWidth={1} style={{ marginBottom: 12, display: "block", margin: "0 auto 12px" }} />
              <p style={{ fontSize: 13 }}>{zoek ? "Geen recepten gevonden." : "Nog geen recepten. Voeg toe via de zijbalk."}</p>
            </div>
          ) : (
            zichtbaar.map(r => (
              <div key={r.id} onClick={() => setSelId(selId === r.id ? null : r.id)}
                style={{ padding: "11px 16px", borderBottom: `1px solid ${C.bdr}`, cursor: "pointer", background: selId === r.id ? C.priBg : C.bg2, borderLeft: `3px solid ${selId === r.id ? C.pri : "transparent"}`, transition: "background .1s" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: selId === r.id ? C.pri : C.txt }}>{r.naam}</div>
                <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                  {r.categorie && <span style={{ fontSize: 10, color: C.txt3 }}>{r.categorie}</span>}
                  {r.allergenen?.length > 0 && (
                    <span style={{ fontSize: 10, color: C.red, display: "flex", alignItems: "center", gap: 3 }}>
                      <TriangleAlert size={9} />{r.allergenen.length} allergeen{r.allergenen.length !== 1 ? "en" : ""}
                    </span>
                  )}
                  {r.ingredienten?.length > 0 && <span style={{ fontSize: 10, color: C.txt3 }}>{r.ingredienten.length} ingrediënten</span>}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Archief toggle */}
        <div style={{ padding: "8px 14px", borderTop: `1px solid ${C.bdr}`, background: C.bg2 }}>
          <button onClick={() => setToonArchief(t => !t)}
            style={{ fontSize: 11, color: C.txt3, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            {toonArchief ? "← Actieve recepten" : `Archief (${recepten.filter(r => r.archived).length})`}
          </button>
        </div>
      </div>

      {/* Rechter paneel — detail */}
      {geselecteerd && (
        <div style={{ flex: 1, overflow: "hidden" }}>
          <ReceptDetail
            recept={geselecteerd}
            categorieen={categorieen}
            onSave={onSave}
            onDelete={onDelete}
            onArchive={onArchive}
            onClose={() => setSelId(null)}
          />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RECEPT DETAIL (lezen + bewerken)
// ─────────────────────────────────────────────────────────────────────────────
function ReceptDetail({ recept, categorieen, onSave, onDelete, onArchive, onClose }) {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <ReceptForm
        recept={recept}
        categorieen={categorieen}
        onSave={(r) => { onSave(r); setEditing(false); }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Detail header */}
      <div style={{ display: "flex", alignItems: "center", padding: "12px 18px", borderBottom: `1px solid ${C.bdr}`, background: C.bg2, gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.txt }}>{recept.naam}</div>
          {recept.categorie && <div style={{ fontSize: 11, color: C.txt3, marginTop: 2 }}>{recept.categorie}</div>}
        </div>
        <button onClick={() => setEditing(true)} style={btnStyle(C.priBg, C.pri, C.priBd)}>✏ Bewerken</button>
        <button onClick={() => { if (confirm(`"${recept.naam}" archiveren?`)) { onArchive(recept.id, true); onClose(); } }}
          style={btnStyle(C.bg3, C.txt2, C.bdr)}>Archiveer</button>
        <button onClick={() => { if (confirm(`"${recept.naam}" definitief verwijderen?`)) { onDelete(recept.id); onClose(); } }}
          style={{ ...btnStyle(C.redBg, C.red, C.redBd), padding: "7px 10px" }}><Trash2 size={13} /></button>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.txt3, padding: 4, display: "flex" }}><X size={18} /></button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
        {/* Foto */}
        {recept.foto_url && (
          <img src={recept.foto_url} alt={recept.naam}
            style={{ width: "100%", maxHeight: 200, objectFit: "cover", borderRadius: 8, marginBottom: 16 }} />
        )}

        {/* Allergenen */}
        {recept.allergenen?.length > 0 && (
          <div style={{ padding: "10px 14px", background: C.redBg, border: `1px solid ${C.redBd}`, borderRadius: 8, marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.red, marginBottom: 6, display: "flex", alignItems: "center", gap: 5 }}>
              <TriangleAlert size={12} /> Allergenen
            </div>
            {/* Hoofd-allergenen */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 4 }}>
              {recept.allergenen.filter(a => !GLUTEN_SUB.includes(a) && !NOTEN_SUB.includes(a)).map(a => (
                <span key={a} style={{ background: C.bg2, border: `1px solid ${C.redBd}`, color: C.red, fontSize: 11, padding: "2px 8px", borderRadius: 10, fontWeight: 600 }}>{a}</span>
              ))}
            </div>
            {/* Gluten subs */}
            {recept.allergenen.some(a => GLUTEN_SUB.includes(a)) && (
              <div style={{ fontSize: 11, color: C.red, marginTop: 4 }}>
                <span style={{ fontWeight: 600 }}>Gluten: </span>
                {recept.allergenen.filter(a => GLUTEN_SUB.includes(a)).join(", ")}
              </div>
            )}
            {/* Noten subs */}
            {recept.allergenen.some(a => NOTEN_SUB.includes(a)) && (
              <div style={{ fontSize: 11, color: C.red, marginTop: 4 }}>
                <span style={{ fontWeight: 600 }}>Noten: </span>
                {recept.allergenen.filter(a => NOTEN_SUB.includes(a)).join(", ")}
              </div>
            )}
          </div>
        )}

        {/* Bewaaradvies */}
        {recept.bewaaradvies && (
          <div style={{ fontSize: 12, color: C.txt2, marginBottom: 16, padding: "8px 12px", background: C.amberBg, border: `1px solid ${C.amberBd}`, borderRadius: 6 }}>
            🌡 {recept.bewaaradvies}
          </div>
        )}

        {/* Ingrediënten */}
        {recept.ingredienten?.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <Sectie titel="Ingrediënten" />
            {recept.ingredienten.map((ing, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${C.bdr}` }}>
                <span style={{ color: C.txt }}>{ing.naam}</span>
                <span style={{ color: C.txt2, fontSize: 12 }}>{ing.hoeveelheid} {ing.eenheid}</span>
              </div>
            ))}
          </div>
        )}

        {/* Bereiding */}
        {recept.bereiding && (
          <div style={{ marginBottom: 20 }}>
            <Sectie titel="Bereiding" />
            <p style={{ fontSize: 13, color: C.txt, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{recept.bereiding}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RECEPT FORM (nieuw + bewerken)
// ─────────────────────────────────────────────────────────────────────────────
function ReceptForm({ recept, categorieen, onSave, onCancel }) {
  const leeg = { id: uid(), naam: "", categorie: "", ingredienten: [], bereiding: "", allergenen: [], bewaaradvies: "", foto_url: "", archived: false };
  const [form, setForm] = useState(recept ? { ...recept } : leeg);
  const [nieuwIng, setNieuwIng] = useState({ naam: "", hoeveelheid: "", eenheid: "kg" });
  const [ingFout, setIngFout] = useState(false);
  const [autoAllergenen, setAutoAllergenen] = useState([]);
  const naamRef = useRef();

  useEffect(() => { naamRef.current?.focus(); }, []);

  // Auto-detecteer allergenen bij ingrediënt-wijzigingen
  useEffect(() => {
    const detected = detectAllergenen(form.ingredienten);
    setAutoAllergenen(detected);
    // Voeg nieuw gedetecteerde toe, verwijder niet-meer-gedetecteerde automatisch TENZIJ handmatig toegevoegd
    setForm(f => ({
      ...f,
      allergenen: [...new Set([...detected, ...(f.allergenen || []).filter(a => !autoAllergenen.includes(a))])]
    }));
  }, [form.ingredienten]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addIng = () => {
    if (!nieuwIng.naam.trim()) { setIngFout(true); return; }
    setForm(f => ({ ...f, ingredienten: [...(f.ingredienten || []), { id: uid(), ...nieuwIng, naam: nieuwIng.naam.trim() }] }));
    setNieuwIng({ naam: "", hoeveelheid: "", eenheid: "kg" });
    setIngFout(false);
  };
  const delIng = (id) => setForm(f => ({ ...f, ingredienten: f.ingredienten.filter(i => i.id !== id) }));

  const toggleAllergeen = (a) => setForm(f => ({
    ...f,
    allergenen: f.allergenen.includes(a) ? f.allergenen.filter(x => x !== a) : [...f.allergenen, a]
  }));

  const opslaan = () => {
    if (!form.naam.trim()) { naamRef.current?.focus(); return; }
    // Voeg niet-opgeklikte ingrediënt toe als er iets ingevuld is
    if (nieuwIng.naam.trim()) {
      const extra = { id: uid(), ...nieuwIng, naam: nieuwIng.naam.trim() };
      onSave({ ...form, ingredienten: [...(form.ingredienten || []), extra] });
    } else {
      onSave(form);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Form header */}
      <div style={{ display: "flex", alignItems: "center", padding: "12px 18px", borderBottom: `1px solid ${C.bdr}`, background: C.bg2, gap: 10 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: C.txt, flex: 1 }}>{recept ? "Recept bewerken" : "Nieuw recept"}</span>
        <button onClick={opslaan} style={btnStyle(C.pri, "#fff", C.pri)}><Save size={14} /> Opslaan</button>
        <button onClick={onCancel} style={{ ...btnStyle(C.bg3, C.txt2, C.bdr), padding: "7px 10px" }}><X size={14} /></button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 20, display: "flex", flexDirection: "column", gap: 18 }}>

        {/* Naam + categorie + bewaaradvies */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Veld label="Naam *" style={{ gridColumn: "1/3" }}>
            <input ref={naamRef} style={inp} placeholder="Naam van het recept" value={form.naam} onChange={e => set("naam", e.target.value)} />
          </Veld>
          <Veld label="Categorie">
            <select style={inp} value={form.categorie} onChange={e => set("categorie", e.target.value)}>
              <option value="">Geen</option>
              {categorieen.map(c => <option key={c}>{c}</option>)}
            </select>
          </Veld>
          <Veld label="Bewaaradvies">
            <select style={inp} value={form.bewaaradvies} onChange={e => set("bewaaradvies", e.target.value)}>
              {BEWAARADVIES_OPTIES.map(o => <option key={o} value={o}>{o || "— geen —"}</option>)}
            </select>
          </Veld>
        </div>

        {/* Foto URL */}
        <Veld label="Foto URL">
          <input style={inp} placeholder="https://... (link naar afbeelding)" value={form.foto_url || ""} onChange={e => set("foto_url", e.target.value)} />
        </Veld>

        {/* Ingrediënten */}
        <div>
          <Sectie titel="Ingrediënten" />
          {(form.ingredienten || []).map(ing => (
            <div key={ing.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: C.bg3, borderRadius: 6, marginBottom: 4 }}>
              <span style={{ flex: 1, fontSize: 13 }}>{ing.naam}</span>
              <span style={{ fontSize: 12, color: C.txt2 }}>{ing.hoeveelheid} {ing.eenheid}</span>
              <button onClick={() => delIng(ing.id)} style={{ background: "none", border: "none", cursor: "pointer", color: C.txt3, padding: 2, display: "flex" }}><Trash2 size={13} /></button>
            </div>
          ))}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 90px auto", gap: 6, marginTop: 8 }}>
            <input style={{ ...inp, ...(ingFout ? { borderColor: C.red } : {}) }} placeholder="Naam ingrediënt *" value={nieuwIng.naam} onChange={e => { setNieuwIng(f => ({ ...f, naam: e.target.value })); setIngFout(false); }} onKeyDown={e => e.key === "Enter" && addIng()} />
            <input style={inp} placeholder="Hoev." type="number" step="0.1" value={nieuwIng.hoeveelheid} onChange={e => setNieuwIng(f => ({ ...f, hoeveelheid: e.target.value }))} />
            <select style={inp} value={nieuwIng.eenheid} onChange={e => setNieuwIng(f => ({ ...f, eenheid: e.target.value }))}>
              {UNITS.map(u => <option key={u}>{u}</option>)}
            </select>
            <button onClick={addIng} style={{ ...btnStyle(C.pri, "#fff", C.pri), padding: "7px 10px" }}><Plus size={15} /></button>
          </div>
        </div>

        {/* Bereiding */}
        <Veld label="Bereiding">
          <textarea style={{ ...inp, minHeight: 120, resize: "vertical", lineHeight: 1.6 }} placeholder="Beschrijf de bereiding…" value={form.bereiding || ""} onChange={e => set("bereiding", e.target.value)} />
        </Veld>

        {/* Allergenen */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <Sectie titel="Allergenen" />
            {autoAllergenen.length > 0 && <span style={{ fontSize: 10, color: C.txt3, fontStyle: "italic" }}>— automatisch gedetecteerd</span>}
          </div>

          {/* 14 hoofd-allergenen */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
            {ALLERGENEN_LIJST.map(a => {
              const actief = form.allergenen?.includes(a);
              const auto = autoAllergenen.includes(a);
              return (
                <button key={a} onClick={() => toggleAllergeen(a)}
                  style={{ padding: "4px 10px", fontSize: 11, fontFamily: "inherit", cursor: "pointer", borderRadius: 12, fontWeight: actief ? 700 : 400, border: `1px solid ${actief ? C.red : C.bdr}`, background: actief ? C.redBg : C.bg2, color: actief ? C.red : C.txt2, position: "relative" }}>
                  {a}
                  {auto && <span style={{ position: "absolute", top: -3, right: -3, width: 7, height: 7, borderRadius: "50%", background: C.amber }} />}
                </button>
              );
            })}
          </div>

          {/* Sub-allergenen Gluten — alleen als Gluten actief */}
          {form.allergenen?.includes("Gluten") && (
            <div style={{ padding: "10px 12px", background: C.bg3, borderRadius: 8, marginBottom: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.txt2, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>
                Gluten — specificeer graansoort
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {GLUTEN_SUB.map(sub => {
                  const actief = form.allergenen?.includes(sub);
                  const auto = autoAllergenen.includes(sub);
                  return (
                    <button key={sub} onClick={() => toggleAllergeen(sub)}
                      style={{ padding: "3px 9px", fontSize: 11, fontFamily: "inherit", cursor: "pointer", borderRadius: 10, fontWeight: actief ? 700 : 400, border: `1px solid ${actief ? C.red : C.bdr2}`, background: actief ? C.redBg : C.bg2, color: actief ? C.red : C.txt2, position: "relative" }}>
                      {sub}
                      {auto && <span style={{ position: "absolute", top: -3, right: -3, width: 6, height: 6, borderRadius: "50%", background: C.amber }} />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sub-allergenen Noten — alleen als Noten actief */}
          {form.allergenen?.includes("Noten") && (
            <div style={{ padding: "10px 12px", background: C.bg3, borderRadius: 8, marginBottom: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.txt2, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>
                Noten — specificeer soort
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {NOTEN_SUB.map(sub => {
                  const actief = form.allergenen?.includes(sub);
                  const auto = autoAllergenen.includes(sub);
                  return (
                    <button key={sub} onClick={() => toggleAllergeen(sub)}
                      style={{ padding: "3px 9px", fontSize: 11, fontFamily: "inherit", cursor: "pointer", borderRadius: 10, fontWeight: actief ? 700 : 400, border: `1px solid ${actief ? C.red : C.bdr2}`, background: actief ? C.redBg : C.bg2, color: actief ? C.red : C.txt2, position: "relative" }}>
                      {sub}
                      {auto && <span style={{ position: "absolute", top: -3, right: -3, width: 6, height: 6, borderRadius: "50%", background: C.amber }} />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <p style={{ fontSize: 11, color: C.txt3, marginTop: 4 }}>Oranje stip = automatisch gedetecteerd. Klik om handmatig aan/uit te zetten.</p>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PASTE VIEW
// ─────────────────────────────────────────────────────────────────────────────
function PasteView({ categorieen, onSave, onCancel }) {
  const [tekst, setTekst] = useState("");
  const [preview, setPreview] = useState(null);
  const [fout, setFout] = useState("");

  const parseer = () => {
    const result = parsePasteRecept(tekst);
    if (!result) { setFout("Kon geen recept herkennen. Zorg dat de eerste regel de naam is, gevolgd door ingrediënten."); return; }
    setFout("");
    setPreview({ id: uid(), ...result, allergenen: detectAllergenen(result.ingredienten), categorie: "", bewaaradvies: "", foto_url: "", archived: false });
  };

  if (preview) {
    return (
      <ReceptForm
        recept={preview}
        categorieen={categorieen}
        onSave={onSave}
        onCancel={() => setPreview(null)}
      />
    );
  }

  return (
    <div style={{ flex: 1, padding: 24, display: "flex", flexDirection: "column", gap: 16, maxWidth: 700 }}>
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.txt, marginBottom: 6 }}>Recept plakken</div>
        <p style={{ fontSize: 12, color: C.txt2, lineHeight: 1.6 }}>
          Kopieer een recept uit een website, Word-document of andere bron en plak het hieronder.
          De eerste regel wordt de naam. Ingrediënten worden automatisch herkend (bijv. "200g gehakt", "2 eieren").
          De rest wordt de bereiding.
        </p>
      </div>

      <textarea
        style={{ ...inp, minHeight: 260, resize: "vertical", fontFamily: "monospace", fontSize: 12, lineHeight: 1.6 }}
        placeholder={`Hamburgers\n\n500g rundergehakt\n1 ei\n2 el paneermeel\nzout en peper\n\nMeng alle ingrediënten...\nVorm balletjes en bak ze...`}
        value={tekst}
        onChange={e => setTekst(e.target.value)}
        autoFocus
      />

      {fout && <div style={{ padding: "10px 14px", background: C.redBg, border: `1px solid ${C.redBd}`, borderRadius: 6, fontSize: 12, color: C.red }}>{fout}</div>}

      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={parseer} disabled={!tekst.trim()} style={{ ...btnStyle(C.pri, "#fff", C.pri), opacity: tekst.trim() ? 1 : 0.5 }}>
          <ClipboardPaste size={14} /> Verwerk recept
        </button>
        <button onClick={onCancel} style={btnStyle(C.bg3, C.txt2, C.bdr)}>Annuleren</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CATEGORIEËN VIEW
// ─────────────────────────────────────────────────────────────────────────────
function CategorieenView({ categorieen, onUpdate }) {
  const [nieuw, setNieuw] = useState("");
  const [edit, setEdit] = useState(null);

  const voegToe = () => {
    if (!nieuw.trim() || categorieen.includes(nieuw.trim())) return;
    onUpdate([...categorieen, nieuw.trim()]);
    setNieuw("");
  };
  const hernoem = () => {
    if (!edit?.naam.trim()) return;
    onUpdate(categorieen.map(c => c === edit.oud ? edit.naam : c));
    setEdit(null);
  };
  const verwijder = (cat) => {
    if (!confirm(`Categorie "${cat}" verwijderen? Recepten in deze categorie krijgen geen categorie.`)) return;
    onUpdate(categorieen.filter(c => c !== cat));
  };

  return (
    <div style={{ flex: 1, padding: 24, maxWidth: 500 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.txt, marginBottom: 16 }}>Categorieën beheren</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {categorieen.map(cat => (
          <div key={cat} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {edit?.oud === cat ? (
              <>
                <input style={{ ...inp, flex: 1 }} value={edit.naam} onChange={e => setEdit(f => ({ ...f, naam: e.target.value }))} autoFocus onKeyDown={e => e.key === "Enter" && hernoem()} />
                <button onClick={hernoem} style={btnStyle(C.pri, "#fff", C.pri)}>OK</button>
                <button onClick={() => setEdit(null)} style={btnStyle(C.bg3, C.txt2, C.bdr)}>✕</button>
              </>
            ) : (
              <>
                <span style={{ flex: 1, fontSize: 13, color: C.txt, padding: "7px 0" }}>{cat}</span>
                <button onClick={() => setEdit({ oud: cat, naam: cat })} style={btnStyle(C.bg3, C.txt2, C.bdr)}>✏</button>
                <button onClick={() => verwijder(cat)} style={{ ...btnStyle(C.redBg, C.red, C.redBd), padding: "7px 10px" }}><Trash2 size={13} /></button>
              </>
            )}
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.bdr}` }}>
        <input style={{ ...inp, flex: 1 }} placeholder="Nieuwe categorie…" value={nieuw} onChange={e => setNieuw(e.target.value)} onKeyDown={e => e.key === "Enter" && voegToe()} />
        <button onClick={voegToe} style={btnStyle(C.pri, "#fff", C.pri)}>+ Toevoegen</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
function Sectie({ titel }) {
  return <div style={{ fontSize: 10, fontWeight: 700, color: C.txt2, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 8 }}>{titel}</div>;
}
function Veld({ label, children, style }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5, ...style }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: C.txt2, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</label>
      {children}
    </div>
  );
}
